import { useZero } from "@rocicorp/zero/react";
import { nanoid } from "nanoid";
import { sharedMutators } from "../../shared/mutators";
import { useForkAsBranch } from "../hooks/useForkAsBranch";
import { useHasAccess } from "../hooks/useHasAccess";
import { navigate, routes, useCurrentUrl } from "../lib/urls";
import { Banner } from "./Banner";
import { Button } from "./Button";
import type { BranchData, CommitData } from "./Workflow";

type Props = {
  branch: BranchData;
  commit: CommitData;
};

export function HistoryBanner({ branch, commit }: Props) {
  const z = useZero();
  const { spaceId, workflowId } = useCurrentUrl(routes.space.workflow);
  // Main is copy-on-write, so restoring it forks a branch like every other edit
  // made from main; only a draft branch rewinds in place.
  const isMain = branch.name === "main";
  const canRestore = useHasAccess("space:write", spaceId);
  const forkAsBranch = useForkAsBranch(spaceId, workflowId);

  const handleRestore = () => {
    if (isMain) {
      forkAsBranch(branch, commit.id);
      return;
    }
    z.mutate(
      sharedMutators.restoreToCommit({
        branchId: branch.id,
        targetCommitId: commit.id,
        newCommitId: nanoid(12),
        chatId: nanoid(12),
        entryId: nanoid(12),
        restoredFromMessage: commit.message,
      })
    );
    navigate(routes.space.workflow, {
      spaceId,
      workflowId,
      branchId: branch.id,
      commitId: "",
    });
  };

  const handleGoToLatest = () => {
    navigate(routes.space.workflow, {
      spaceId,
      workflowId,
      branchId: branch.id,
      commitId: "",
    });
  };

  return (
    <Banner
      actions={
        <>
          <Button
            variant="secondary"
            icon="redo-2"
            onClick={handleGoToLatest}
          />
          {canRestore && (
            <Button onClick={handleRestore}>
              {isMain ? "Restore to new branch" : "Restore"}
            </Button>
          )}
        </>
      }
    >
      This is a historical version of this workflow
    </Banner>
  );
}
